import { ForwardedRef, Ref, forwardRef, useMemo } from 'react';

import { CheckCircleFilled, LoadingOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { Flex } from '@revenuemarket/cds';
import { Colors, typographyStyle } from '@revenuemarket/design';
import { Input, InputProps, Typography } from 'antd';
import { InputRef, TextAreaProps } from 'antd/es/input';

import { ExclamationCircleIcon } from '../../Icon';

export type InputStatus = 'error' | 'warning' | 'success' | 'loading';

interface Props extends InputProps {
  inputStatus?: InputStatus;
  disabled?: boolean;
  additionalText?: string;
  inputRef?: Ref<InputRef>;
}

const getSuffixIcon = (inputType?: InputStatus, disabled?: boolean) => {
  switch (inputType) {
    case 'error':
      return <ExclamationCircleIcon fill={Colors.red500} width={16} height={16} />;
    case 'warning':
      return <ExclamationCircleIcon fill={Colors.orange500} width={16} height={16} />;
    case 'success':
      return (
        <CheckCircleFilled
          style={{
            color: Colors.green500,
            fontSize: '16px',
          }}
        />
      );

    case 'loading':
      return (
        <LoadingOutlined
          style={{
            fontSize: '16px',
          }}
        />
      );

    default:
      return '';
  }
};

const V2Input = ({ inputStatus, additionalText, ...props }: Props, ref: ForwardedRef<any>) => {
  const suffixIcon = useMemo(
    () => getSuffixIcon(inputStatus, props.disabled),
    [inputStatus, props.disabled],
  );

  return (
    <Flex direction="column" gap={additionalText ? 4 : 0}>
      <Input
        ref={ref || props.inputRef}
        className={css`
          &:hover {
            border-color: ${Colors.gray400} !important;
          }
          &:focus {
            border-color: ${Colors.gray400} !important;
            box-shadow: 0 0 0 1px ${Colors.gray400} !important;
          }
        `}
        suffix={<div key="suffix">{suffixIcon}</div>}
        {...props}
      />
      <Typography.Text
        style={{
          color: inputStatus === 'warning' ? Colors.orange600 : Colors.red700,
          ...typographyStyle['14_400'],
        }}
      >
        {additionalText}
      </Typography.Text>
    </Flex>
  );
};

export default forwardRef(V2Input);

interface TextareaProps extends TextAreaProps {
  inputStatus?: InputStatus;
  disabled?: boolean;
  additionalText?: string;
}

export const TextArea: React.FC<TextareaProps> = ({ inputStatus, additionalText, ...props }) => {
  return (
    <Flex direction="column" gap={additionalText ? 4 : 0}>
      <Input.TextArea
        className={css`
          &:hover {
            border-color: ${Colors.gray400} !important;
          }
          &:focus {
            border-color: ${Colors.gray400} !important;
            box-shadow: 0 0 0 1px ${Colors.gray400} !important;
          }
        `}
        {...props}
      />
      <Typography.Text
        style={{
          color: inputStatus === 'warning' ? Colors.orange600 : Colors.red700,
          ...typographyStyle['14_400'],
        }}
      >
        {additionalText}
      </Typography.Text>
    </Flex>
  );
};
